import { createAction } from '@shared/context/reducerUtils';
import { format, isValid, parse } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  GUEST_ID_PARAM,
  MESSAGING_PARAM,
  WALK_IN_ID_PARAM,
} from './../../../messaging/hooks';
import { type ServiceOccupant } from '../apiHelpers';
import {
  ADD_TO_WAITLIST,
  DATE_PARAM,
  DATE_PARAM_FORMAT,
  DRAWER_PARAM,
  EDIT_RESERVATION_MODE_PARAM,
  GUEST_SEARCH_PARAM,
  OCCUPANT_DETAILS,
  OCCUPANT_ID_PARAM,
  OCCUPANT_TYPE_PARAM,
  SEAT_MODE_PARAM,
  SEAT_RESERVATION_GUEST,
  SEAT_WAITLIST_GUEST,
  SEAT_WALK_IN_FORM,
  SERVER_FORM,
  SERVICE_OVERVIEW,
  STATS,
  VIEW_WAITLIST_ENTRY,
  WAITLIST_ENTRY_INDEX_PARAM,
} from '../../../utils/constants';

enum ServiceSearchParamsActionType {
  SearchForGuest = 'SearchForGuest',
  OpenOccupantDetailsDrawer = 'OpenOccupantDetailsDrawer',
  OpenAddToWaitListDrawer = 'OpenAddToWaitListDrawer',
  OpenWaitListEntryDrawer = 'OpenWaitListEntryDrawer',
  OpenServerDrawer = 'OpenServerDrawer',
  OpenSeatWalkInFormDrawer = 'OpenSeatWalkInFormDrawer',
  OpenWaitListSeatDrawer = 'OpenWaitListSeatDrawer',
  OpenSeatReservationDrawer = 'OpenSeatReservationDrawer',
  OpenServiceOverviewDrawer = 'OpenServiceOverviewDrawer',
  OpenReservationTimelineBookDrawer = 'ReservationTimelineBookDrawer',
  EnterReservationEditMode = 'EnterReservationEditMode',
  ExitReservationEditMode = 'ExitReservationEditMode',
  EnterSeatMode = 'EnterSeatMode',
  OpenStatsDrawer = 'OpenStatsDrawer',
  ExitSeatMode = 'ExitSeatMode',
  CloseDrawer = 'CloseDrawer',
  UpdateDate = 'UpdateDate',
  ClearAll = 'ClearAll',
}

export const searchForGuest = (guestSearch: string) => {
  return createAction(ServiceSearchParamsActionType.SearchForGuest, {
    guestSearch,
  });
};

const openOccupantDetailsDrawer = ({
  id,
  type,
}: Pick<ServiceOccupant, 'id' | 'type'>) =>
  createAction(ServiceSearchParamsActionType.OpenOccupantDetailsDrawer, {
    id,
    type,
  });

const openAddToWaitListDrawer = () =>
  createAction(ServiceSearchParamsActionType.OpenAddToWaitListDrawer, {});

const openServerDrawer = () =>
  createAction(ServiceSearchParamsActionType.OpenServerDrawer, {});

const openSeatWalkInFormDrawer = (selectedTableId?: string) =>
  createAction(ServiceSearchParamsActionType.OpenSeatWalkInFormDrawer, {
    selectedTableId,
  });
const openStatsDrawer = () =>
  createAction(ServiceSearchParamsActionType.OpenStatsDrawer, {});

const openWaitListEntryDrawer = (index: number) =>
  createAction(ServiceSearchParamsActionType.OpenWaitListEntryDrawer, {
    index,
  });

const openWaitListSeatDrawer = () =>
  createAction(ServiceSearchParamsActionType.OpenWaitListSeatDrawer, null);
const openSeatReservationDrawer = () =>
  createAction(ServiceSearchParamsActionType.OpenSeatReservationDrawer, null);
const openServiceOverviewDrawer = () =>
  createAction(ServiceSearchParamsActionType.OpenServiceOverviewDrawer, null);

const openReservationTimelineBookDrawer = () =>
  createAction(
    ServiceSearchParamsActionType.OpenReservationTimelineBookDrawer,
    null,
  );

const enterReservationEditMode = () =>
  createAction(ServiceSearchParamsActionType.EnterReservationEditMode, null);

const exitReservationEditMode = () =>
  createAction(ServiceSearchParamsActionType.ExitReservationEditMode, null);

const exitSeatMode = () =>
  createAction(ServiceSearchParamsActionType.ExitSeatMode, null);
const enterSeatMode = () =>
  createAction(ServiceSearchParamsActionType.EnterSeatMode, null);
const closeDrawer = () =>
  createAction(ServiceSearchParamsActionType.CloseDrawer, null);
const updateDate = (date: Date) =>
  createAction(ServiceSearchParamsActionType.UpdateDate, { date });
const clearAll = () =>
  createAction(ServiceSearchParamsActionType.ClearAll, null);

const serviceSearchParamsReducer = (
  params: URLSearchParams,
  action:
    | ReturnType<typeof searchForGuest>
    | ReturnType<typeof openOccupantDetailsDrawer>
    | ReturnType<typeof openAddToWaitListDrawer>
    | ReturnType<typeof openWaitListEntryDrawer>
    | ReturnType<typeof openServerDrawer>
    | ReturnType<typeof openSeatWalkInFormDrawer>
    | ReturnType<typeof openWaitListSeatDrawer>
    | ReturnType<typeof openSeatReservationDrawer>
    | ReturnType<typeof openServiceOverviewDrawer>
    | ReturnType<typeof openReservationTimelineBookDrawer>
    | ReturnType<typeof openStatsDrawer>
    | ReturnType<typeof enterReservationEditMode>
    | ReturnType<typeof exitReservationEditMode>
    | ReturnType<typeof exitSeatMode>
    | ReturnType<typeof enterSeatMode>
    | ReturnType<typeof closeDrawer>
    | ReturnType<typeof updateDate>
    | ReturnType<typeof clearAll>,
): URLSearchParams => {
  const newParams = new URLSearchParams(params);

  switch (action.type) {
    case ServiceSearchParamsActionType.SearchForGuest:
      newParams.set(GUEST_SEARCH_PARAM, action.payload.guestSearch);
      return newParams;
    case ServiceSearchParamsActionType.OpenOccupantDetailsDrawer:
      newParams.set(OCCUPANT_ID_PARAM, action.payload.id);
      newParams.set(OCCUPANT_TYPE_PARAM, action.payload.type);
      newParams.set(DRAWER_PARAM, OCCUPANT_DETAILS);
      newParams.delete(EDIT_RESERVATION_MODE_PARAM);
      return newParams;
    case ServiceSearchParamsActionType.EnterReservationEditMode:
      newParams.set(EDIT_RESERVATION_MODE_PARAM, 'true');
      return newParams;
    case ServiceSearchParamsActionType.ExitReservationEditMode:
      newParams.delete(EDIT_RESERVATION_MODE_PARAM);
      return newParams;
    case ServiceSearchParamsActionType.OpenAddToWaitListDrawer:
      newParams.set(DRAWER_PARAM, ADD_TO_WAITLIST);
      return newParams;
    case ServiceSearchParamsActionType.OpenWaitListEntryDrawer:
      newParams.set(DRAWER_PARAM, VIEW_WAITLIST_ENTRY);
      newParams.set(WAITLIST_ENTRY_INDEX_PARAM, String(action.payload.index));
      return newParams;
    case ServiceSearchParamsActionType.OpenSeatWalkInFormDrawer:
      newParams.set(DRAWER_PARAM, SEAT_WALK_IN_FORM);
      newParams.set(SEAT_MODE_PARAM, 'true');
      return newParams;
    case ServiceSearchParamsActionType.OpenServerDrawer:
      newParams.set(DRAWER_PARAM, SERVER_FORM);
      return newParams;
    case ServiceSearchParamsActionType.OpenWaitListSeatDrawer:
      newParams.set(SEAT_MODE_PARAM, 'true');
      newParams.set(DRAWER_PARAM, SEAT_WAITLIST_GUEST);
      return newParams;
    case ServiceSearchParamsActionType.OpenSeatReservationDrawer:
      newParams.set(SEAT_MODE_PARAM, 'true');
      newParams.set(DRAWER_PARAM, SEAT_RESERVATION_GUEST);
      return newParams;
    case ServiceSearchParamsActionType.OpenStatsDrawer:
      newParams.set(DRAWER_PARAM, STATS);
      return newParams;
    case ServiceSearchParamsActionType.OpenServiceOverviewDrawer:
      newParams.set(DRAWER_PARAM, SERVICE_OVERVIEW);
      return newParams;
    case ServiceSearchParamsActionType.EnterSeatMode:
      // It doesn't really matter what the seat mode param value is, just as
      // long as its present
      newParams.set(SEAT_MODE_PARAM, 'true');
      newParams.delete(WAITLIST_ENTRY_INDEX_PARAM);
      newParams.delete(DRAWER_PARAM);
      return newParams;
    case ServiceSearchParamsActionType.ExitSeatMode:
      newParams.delete(SEAT_MODE_PARAM);
      newParams.delete(OCCUPANT_ID_PARAM);
      newParams.delete(OCCUPANT_TYPE_PARAM);
      newParams.delete(DRAWER_PARAM);
      return newParams;
    case ServiceSearchParamsActionType.CloseDrawer:
      newParams.delete(OCCUPANT_ID_PARAM);
      newParams.delete(OCCUPANT_TYPE_PARAM);
      newParams.delete(WAITLIST_ENTRY_INDEX_PARAM);
      newParams.delete(DRAWER_PARAM);
      // Clear messaging drawer params to avoid racy bug where click outside cause stray messaging params to re-appear when both drawers are open at once
      // see https://app.shortcut.com/peak-reservations/story/13665/bug-closing-the-messaging-drawer-opened-from-the-occupant-details-drawer-on-the-service-page-can-be-reopened-when-changing
      newParams.delete(MESSAGING_PARAM);
      newParams.delete(GUEST_ID_PARAM);
      newParams.delete(WALK_IN_ID_PARAM);
      newParams.delete(SEAT_MODE_PARAM);
      return newParams;
    case ServiceSearchParamsActionType.UpdateDate:
      return new URLSearchParams({
        [DATE_PARAM]: format(action.payload.date, DATE_PARAM_FORMAT),
      });
    case ServiceSearchParamsActionType.ClearAll:
      return new URLSearchParams();
    default:
      throw new Error(`Unhandled action type`);
  }
};

export const useServiceSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const dispatch = (
    action: Parameters<typeof serviceSearchParamsReducer>[1],
  ) => {
    const newParams = serviceSearchParamsReducer(searchParams, action);
    setSearchParams(newParams);
  };

  const inOccupantDetailsMode =
    searchParams.get(DRAWER_PARAM) === OCCUPANT_DETAILS &&
    searchParams.has(OCCUPANT_ID_PARAM) &&
    searchParams.has(OCCUPANT_TYPE_PARAM);

  const inAddToWaitlistMode =
    searchParams.get(DRAWER_PARAM) === ADD_TO_WAITLIST;

  const inViewWaitlistEntryMode =
    searchParams.get(DRAWER_PARAM) === VIEW_WAITLIST_ENTRY;

  const inServerMode = searchParams.get(DRAWER_PARAM) === SERVER_FORM;

  const inSeatWaitlistGuestMode =
    searchParams.get(DRAWER_PARAM) === SEAT_WAITLIST_GUEST;

  const inChangeWalkInSeatMode =
    searchParams.get(DRAWER_PARAM) === SEAT_WALK_IN_FORM &&
    searchParams.has(OCCUPANT_ID_PARAM);

  const inSeatWalkInFormMode =
    searchParams.get(DRAWER_PARAM) === SEAT_WALK_IN_FORM &&
    !searchParams.has(OCCUPANT_ID_PARAM);

  const inServiceOverviewMode =
    searchParams.get(DRAWER_PARAM) === SERVICE_OVERVIEW;

  const inSeatReservationGuestMode =
    searchParams.get(DRAWER_PARAM) === SEAT_RESERVATION_GUEST &&
    searchParams.has(OCCUPANT_ID_PARAM) &&
    searchParams.has(OCCUPANT_TYPE_PARAM);

  const inStatsMode = searchParams.get(DRAWER_PARAM) === STATS;

  const occupantId = searchParams.get(OCCUPANT_ID_PARAM);
  const occupantType = searchParams.get(OCCUPANT_TYPE_PARAM);
  const occupantTypeIsReservation =
    searchParams.get(OCCUPANT_TYPE_PARAM) === 'reservation';
  const occupantTypeIsWalkIn =
    searchParams.get(OCCUPANT_TYPE_PARAM) === 'walkIn';

  const inSeatOccupantMode = searchParams.has(SEAT_MODE_PARAM);

  const drawerType = searchParams.get(DRAWER_PARAM);
  const drawerIsOpen = searchParams.has(DRAWER_PARAM);

  const guestSearch = searchParams.get(GUEST_SEARCH_PARAM);

  const dateStringFromSearchParams = searchParams.get(DATE_PARAM);
  const parsedDate = dateStringFromSearchParams
    ? parse(dateStringFromSearchParams, DATE_PARAM_FORMAT, new Date())
    : null;
  const hasValidDateParam =
    dateStringFromSearchParams !== null &&
    parsedDate !== null &&
    isValid(parsedDate);
  const date = hasValidDateParam ? parsedDate! : new Date();

  return {
    searchParams,
    guestSearch,
    date,
    drawerIsOpen,
    drawerType,
    inSeatOccupantMode,
    inOccupantDetailsMode,
    inAddToWaitlistMode,
    inViewWaitlistEntryMode,
    inServerMode,
    inSeatWaitlistGuestMode,
    inChangeWalkInSeatMode,
    inSeatWalkInFormMode,
    inServiceOverviewMode,
    inSeatReservationGuestMode,
    inStatsMode,
    occupantId,
    occupantType,
    occupantTypeIsReservation,
    occupantTypeIsWalkIn,
    searchForGuest: (guestSearch: string) =>
      dispatch(searchForGuest(guestSearch)),
    openOccupantDetailsDrawer: (
      params: Parameters<typeof openOccupantDetailsDrawer>[0],
    ) => dispatch(openOccupantDetailsDrawer(params)),
    openAddToWaitListDrawer: () => dispatch(openAddToWaitListDrawer()),
    openSeatWalkInFormDrawer: (selectedTableId?: string) =>
      dispatch(openSeatWalkInFormDrawer(selectedTableId)),
    openServerDrawer: () => dispatch(openServerDrawer()),
    openStatsDrawer: () => dispatch(openStatsDrawer()),
    openWaitListEntryDrawer: (index: number) =>
      dispatch(openWaitListEntryDrawer(index)),
    openWaitListSeatDrawer: () => dispatch(openWaitListSeatDrawer()),
    openSeatReservationDrawer: () => dispatch(openSeatReservationDrawer()),
    openServiceOverviewDrawer: () => dispatch(openServiceOverviewDrawer()),
    openReservationTimelineBookDrawer: () =>
      dispatch(openReservationTimelineBookDrawer()),
    enterReservationEditMode: () => dispatch(enterReservationEditMode()),
    exitReservationEditMode: () => dispatch(exitReservationEditMode()),
    exitSeatMode: () => dispatch(exitSeatMode()),
    enterSeatMode: () => dispatch(enterSeatMode()),
    navigateBack: () => navigate(-1),
    closeDrawer: () => dispatch(closeDrawer()),
    updateDate: (date: Date) => dispatch(updateDate(date)),
    clearAll: () => dispatch(clearAll()),
  };
};
